import React from "react";
import { Field } from "react-final-form";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import type { PasswordFieldName } from "./changePasswordValidation";

type PasswordFieldProps = {
    name: PasswordFieldName;
    label: string;
    autoComplete: string;
    visible: boolean;
    showPasswordLabel: string;
    hidePasswordLabel: string;
    onToggle: () => void;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
    name,
    label,
    autoComplete,
    visible,
    showPasswordLabel,
    hidePasswordLabel,
    onToggle,
}) => {
    const errorId = `${name}-error`;

    return (
        <Field name={name}>
            {({ input, meta }) => {
                const error = (meta.touched && meta.error) || (!meta.modifiedSinceLastSubmit && meta.submitError);

                return (
                    <div className="flex flex-col gap-2">
                        <Label htmlFor={name}>{label}</Label>
                        <div className="relative">
                            <Input
                                {...input}
                                id={name}
                                type={visible ? "text" : "password"}
                                autoComplete={autoComplete}
                                aria-invalid={Boolean(error)}
                                aria-describedby={error ? errorId : undefined}
                                className="h-11 rounded-xl pr-12"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 text-slate-500 hover:bg-transparent hover:text-slate-900"
                                onClick={onToggle}
                                aria-label={visible ? hidePasswordLabel : showPasswordLabel}
                            >
                                {visible ? (
                                    <EyeOff className="h-4 w-4" aria-hidden />
                                ) : (
                                    <Eye className="h-4 w-4" aria-hidden />
                                )}
                            </Button>
                        </div>
                        {error && (
                            <p id={errorId} aria-live="polite" className="text-sm text-destructive">
                                {error}
                            </p>
                        )}
                    </div>
                );
            }}
        </Field>
    );
};

export default PasswordField;
